import React, { useState, useEffect, useRef } from "react";

// Helper function: Get first day (Sunday) of the week for a given date
function getFirstDayOfWeek(date) {
  // console.log(date);

  const d = new Date(date);
  d.setFullYear(d.getUTCFullYear());
  d.setMonth(d.getUTCMonth());
  d.setDate(d.getUTCDate());
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  const day = d.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
  const diff = (day + 6) % 7; // 0 (Sun) -> 6, 1 (Mon) -> 0, ..., 6 (Sat) -> 5
  d.setDate(d.getDate() - diff);
  return d;
}

// Format date to display in header: "12 May - 8 Jun"
function formatDateRange(date, period) {
  const startDate = new Date(date);
  const endDate = new Date(date);
  endDate.setDate(endDate.getDate() + period - 1);

  const startDay = startDate.getDate();
  const startMonth = startDate.toLocaleString("default", { month: "short" });
  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleString("default", { month: "short" });

  return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
}

function WeekNavigator({ selectedDate, setSelectedDate, period = 28 }) {
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(
    getFirstDayOfWeek(selectedDate)
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
  );
  const calendarRef = useRef(null);

  useEffect(() => {
    setSelectedDate(firstDayOfWeek);
  }, [firstDayOfWeek, setSelectedDate]);

  useEffect(() => {
    // Close calendar when clicking outside
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigation handlers
  const handlePrevWeek = () => {
    const prevWeek = new Date(firstDayOfWeek);
    prevWeek.setDate(prevWeek.getDate() - period + 1);
    setFirstDayOfWeek(getFirstDayOfWeek(prevWeek));
  };

  const handleNextWeek = () => {
    const nextWeek = new Date(firstDayOfWeek);
    nextWeek.setDate(nextWeek.getDate() + period + 1);
    setFirstDayOfWeek(getFirstDayOfWeek(nextWeek));
  };

  // Calendar navigation
  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // When date is selected from calendar
  const handleDateSelection = (date) => {
    setFirstDayOfWeek(getFirstDayOfWeek(date));
    setCalendarOpen(false);
  };

  const handleJumpToCurrentWeek = () => {
    setFirstDayOfWeek(getFirstDayOfWeek(new Date()));
    setCalendarOpen(false);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Get days in month and first day of month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let firstDayOfMonth = new Date(year, month, 1).getDay();
    firstDayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Shift so Monday = 0

    const daysFromPrevMonth = firstDayOfMonth;

    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();

    const days = [];

    // Previous month days
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      days.push({
        date: new Date(year, month - 1, day),
        day,
        isCurrentMonth: false,
      });
    }

    // Current month days
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        day: i,
        isCurrentMonth: true,
        isToday:
          today.getDate() === i &&
          today.getMonth() === month &&
          today.getFullYear() === year,
        isSelected:
          firstDayOfWeek.getDate() === i &&
          firstDayOfWeek.getMonth() === month &&
          firstDayOfWeek.getFullYear() === year,
      });
    }

    // Next month days to fill the calendar grid (6 rows * 7 columns = 42 cells)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        day: i,
        isCurrentMonth: false,
      });
    }

    return days;
  };

  return (
    <div className="relative mx-2" ref={calendarRef}>
      <div className="flex items-center gap-1">
        <button
          onClick={handlePrevWeek}
          className="text-gray-700 p-2 bg-gray-50 hover:bg-gray-100 rounded-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <button
          onClick={() => setCalendarOpen(!calendarOpen)}
          className="text-gray-800 font-medium text-sm px-8 py-2 rounded-md bg-gray-50 hover:bg-gray-100 transition"
        >
          {formatDateRange(firstDayOfWeek, period)}
        </button>

        <button
          onClick={handleNextWeek}
          className="text-gray-700 p-2 bg-gray-50  hover:bg-gray-100 rounded-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Calendar dropdown */}
      {calendarOpen && (
        <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-72">
          <div className="p-4">
            {/* Calendar header */}
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={handlePrevMonth}
                className="text-gray-600 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <span className="font-medium text-gray-800">
                {currentMonth.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </span>

              <button
                onClick={handleNextMonth}
                className="text-gray-600 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div key={day} className="text-xs font-medium text-gray-700">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays().map((day, index) => (
                <button
                  key={index}
                  onClick={() => handleDateSelection(day.date)}
                  className={`
                      h-8 w-8 rounded-full flex items-center justify-center text-xs 
                      ${
                        !day.isCurrentMonth ? "text-gray-400" : "text-gray-800"
                      } 
                      ${day.isSelected ? "bg-gray-200" : ""} 
                      ${
                        day.isToday && !day.isSelected
                          ? "bg-gray-100 font-bold"
                          : ""
                      }
                      hover:bg-gray-100
                    `}
                >
                  {day.day}
                </button>
              ))}
            </div>

            {/* Jump to current week button */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-center">
              <button
                onClick={handleJumpToCurrentWeek}
                className="text-gray-700 text-sm font-medium py-2 px-4 hover:bg-gray-50 rounded transition"
              >
                Jump to Current Week
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeekNavigator;
